import { computed, linkedSignal, signal, Signal, WritableSignal } from '@angular/core';
import { ViewPreset } from '@shared/dataview/core/dataview-types';
import { DataViewDraftStorage } from '@shared/dataview/state/draft-storage';
import { findLast, isEqual, keyBy } from 'lodash';

export class DefaultViewOverwriteError extends Error {
  constructor(readonly viewId: string) {
    super(`"${viewId}" is a default view and cannot be overwritten. Save your changes as a new view instead.`);
    this.name = 'DefaultViewOverwriteError';
  }
}

export interface ViewPresetStoreOptions<TState> {
  pageName: string;
  views: Signal<Array<ViewPreset<TState>>>;
  storage: DataViewDraftStorage;
  initialViewId?: string;
  normalize?: (state: TState) => TState;
}

export class ViewPresetStore<TState> {
  readonly views: WritableSignal<Array<ViewPreset<TState>>>;
  readonly pinnedViews: Signal<Array<ViewPreset<TState>>>;
  readonly currentId: Signal<string | undefined>;
  readonly current: Signal<ViewPreset<TState> | undefined>;
  readonly draft: Signal<TState | undefined>;
  readonly hasUnsavedChanges: Signal<boolean>;
  readonly canSaveCurrent: Signal<boolean>;

  private readonly currentIdState: WritableSignal<string | undefined>;
  // Not a linkedSignal on the current view: a write would leave it stale when the view is selected again.
  private readonly draftOverride = signal<{ viewId: string; state: TState } | undefined>(undefined);

  constructor(private readonly options: ViewPresetStoreOptions<TState>) {
    this.views = linkedSignal<Array<ViewPreset<TState>>>(() => options.views());

    this.currentIdState = linkedSignal<Array<ViewPreset<TState>>, string | undefined>({
      source: () => this.views(),
      computation: (views, previous) =>
        previous?.value && views.some(view => view.id === previous.value) ? previous.value : this.resolveInitialId(views),
    });

    this.currentId = this.currentIdState.asReadonly();
    this.current = computed(() => this.views().find(view => view.id === this.currentId()));
    this.pinnedViews = computed(() => this.views().filter(view => view.isPinned));

    this.draft = computed(() => {
      const view = this.current();
      if (!view) return undefined;

      const override = this.draftOverride();
      if (override?.viewId === view.id) return override.state;

      return options.storage.readDraft<TState>(options.pageName, view.id) ?? view.state;
    });

    this.hasUnsavedChanges = computed(() => {
      const current = this.current();
      return current !== undefined && !isEqual(this.draft(), this.baseline(current));
    });

    this.canSaveCurrent = computed(() => this.current()?.isCustom === true);
  }

  select(viewId: string): void {
    if (!this.views().some(view => view.id === viewId)) return;
    this.currentIdState.set(viewId);
    this.options.storage.writeLastViewId(this.options.pageName, viewId);
  }

  setDraft(state: TState): void {
    const view = this.current();
    if (!view) return;

    this.draftOverride.set({ viewId: view.id, state });

    if (isEqual(state, this.baseline(view))) {
      this.options.storage.clearDraft(this.options.pageName, view.id);
      return;
    }

    this.options.storage.writeDraft(this.options.pageName, view.id, state);
  }

  resetDraft(): void {
    const view = this.current();
    if (!view) return;

    this.options.storage.clearDraft(this.options.pageName, view.id);
    this.draftOverride.set(undefined);
  }

  saveCurrent(label?: string): ViewPreset<TState> | undefined {
    const view = this.current();
    const draft = this.draft();
    if (!view || draft === undefined) return undefined;
    if (!view.isCustom) throw new DefaultViewOverwriteError(view.id);

    const saved: ViewPreset<TState> = { ...view, label: label ?? view.label, state: draft };
    this.views.update(views => views.map(existing => (existing.id === view.id ? saved : existing)));
    this.options.storage.clearDraft(this.options.pageName, view.id);
    this.draftOverride.set(undefined);

    return saved;
  }

  saveAsNew(options: { id: string; label: string; ownerUsername?: string }): ViewPreset<TState> | undefined {
    const view = this.current();
    const draft = this.draft();
    if (draft === undefined) return undefined;

    const created: ViewPreset<TState> = {
      id: options.id,
      label: options.label,
      state: draft,
      isPinned: true,
      isCustom: true,
      ownerUsername: options.ownerUsername,
      basedOnViewId: view?.id,
    };

    if (view) this.options.storage.clearDraft(this.options.pageName, view.id);
    this.draftOverride.set(undefined);
    this.views.update(views => [...views, created]);
    this.select(created.id);

    return created;
  }

  rearrange(arrangement: Array<Pick<ViewPreset<TState>, 'id' | 'isPinned'>>): void {
    const byId = keyBy(this.views(), 'id');
    const keptIds = new Set(arrangement.map(entry => entry.id));

    for (const view of this.views()) {
      if (!keptIds.has(view.id)) this.options.storage.clearDraft(this.options.pageName, view.id);
    }

    this.views.set(arrangement.flatMap(entry => (byId[entry.id] ? [{ ...byId[entry.id], isPinned: !!entry.isPinned }] : [])));

    const lastPinned = findLast(this.views(), view => !!view.isPinned);
    if (!this.current()?.isPinned && lastPinned) this.select(lastPinned.id);
  }

  private baseline(view: ViewPreset<TState>): TState {
    return this.options.normalize ? this.options.normalize(view.state) : view.state;
  }

  private resolveInitialId(views: Array<ViewPreset<TState>>): string | undefined {
    const has = (id: string | undefined) => id !== undefined && views.some(view => view.id === id);

    if (has(this.options.initialViewId)) return this.options.initialViewId;

    const lastOpened = this.options.storage.readLastViewId(this.options.pageName);
    if (has(lastOpened)) return lastOpened;

    return (views.find(view => view.isPinned) ?? views[0])?.id;
  }
}
